import {
  Assets,
  Color,
  Container,
  FillGradient,
  Graphics,
  Matrix,
  Texture,
} from "pixi.js";
import { PIXELS_PER_INCH } from "../shop-scale";
import {
  Board,
  BOARD_FACE_SCAN_LENGTH_IN,
  BOARD_FACE_SCAN_WIDTH_IN,
  defaultBoardFace,
  miterSkews,
} from "../../game/Materials";
import { lerp } from "../../utils/mathUtils";
import { seededRandom } from "../../utils/randUtils";
import { BOARD_ROUGHNESS_TEXTURES } from "./boardFaceTextures";
import {
  edgeFill,
  endFill,
  faceFill,
  TURNED_AWAY_SHADE,
  woodArt,
} from "./woodFills";

/**
 * The board renderers — the old BoardSprite / BoardOnEdgeSprite /
 * BoardOnEndSprite as imperative builders and draw functions. Same
 * drawing, no React: callers own the returned display objects.
 */

/** The gray of weathered, unmilled lumber — species color hides under it. */
const WEATHERED_GRAY = 0x9a9186;
/** How heavily that gray sits on wood the cutter hasn't been over. */
const WEATHERED_ALPHA = 0.62;

/** The board data the renderers read — everything but identity. */
export type BoardLook = Omit<Board, "id" | "type">;

export interface BoardSpriteOptions {
  alpha?: number;
  tint?: number;
}

/**
 * A board's milled state is drawn, not labeled:
 * - Every species wears a window of a real plank photograph
 *   (boardFaceTextures.ts), placed by the board's face region so a cut
 *   piece keeps the very grain it was cut with.
 * - Seen from above, a lying board is nothing but its face — thickness
 *   reads through the contact shadow's spread, not a drawn edge strip.
 *   The edge face only shows on a board actually stood on it
 *   (drawBoardOnEdge), which is what keeps the two orientations
 *   distinct at a glance.
 * - Roughness is real too: a grayscale wear map multiplies over the face
 *   (white is clean wood, dark is scuffs and saw marks), windowed by the
 *   same region so a board's scars survive its cuts.
 * - Unjointed faces on rough stock additionally hide under a
 *   weathered-gray veil; jointing (or sanding) lifts it and the same
 *   grain comes up in color.
 * - Unjointed long edges are wavy; jointing snaps them straight, right
 *   edge first.
 * - The rungs between those two extremes each look like the work that
 *   made them. Milling is two axes, so a board can have a jointed face
 *   and a sawn edge at once: the veil retreats to a band hugging the
 *   edge still waiting for the jointer, which is the same fact that
 *   edge's waviness is already reporting. A planed face is clear of it
 *   and carries only a trace of the wear map, which is exactly what a
 *   sanded face is missing. Sanded is bare, flat, and a shade paler than
 *   off the planer, the way raw sanded wood really is; oiling
 *   (finishLook.ts) is the rung that pays that back.
 * - Mitered ends draw as diagonals: the left end is the sprite's top, the
 *   right end its bottom, and the angle sets the diagonal's run.
 * All irregularity is seeded so a board never shimmers between renders.
 *
 * A Container of two Graphics — the base drawing, and the wear map
 * multiplied over it — because a multiply blend needs its own layer.
 */
export function createBoardSprite(
  board: BoardLook,
  seed?: string,
  options: BoardSpriteOptions = {},
): Container {
  const { thickness, species, surface, jointedFaces } = board;
  const { alpha, tint } = options;

  const fallbackSeed = seed ?? boardFallbackSeed(board);
  const colorRevealed = jointedFaces > 0 || surface !== "rough";

  // The board's window onto its source plank (inches on the canonical
  // scan rectangle) — drives the face art AND the wear map, so both
  // survive a cut together.
  const region =
    board.face ?? defaultBoardFace(fallbackSeed, board.length, board.width);

  const silhouette = boardSilhouette(board, fallbackSeed);
  const { width, height } = silhouette;

  const root = new Container();
  if (alpha !== undefined) root.alpha = alpha;
  const g = root.addChild(new Graphics());
  if (tint !== undefined) g.tint = tint;

  const art = woodArt(species, region.seed, thickness);
  g.poly(silhouette.facePoly);
  g.fill(faceFill(art, region, -width / 2, -height / 2));

  // Weathering reads as a veil over the real grain: it covers an
  // unmilled face, hangs on along an edge the milling hasn't got to
  // yet, and is gone once a planer or a sander has been over the whole
  // thing. Sanding lightens what it flattens — raw sanded wood really
  // is paler than wood off the planer, and oiling is what pays that
  // back (see finishLook.ts).
  const band =
    colorRevealed && surface === "rough" ? weatheredBand(board) : null;
  if (!colorRevealed) {
    g.poly(silhouette.facePoly);
    g.fill({ color: WEATHERED_GRAY, alpha: WEATHERED_ALPHA });
  } else if (band) {
    g.poly(silhouette.facePoly);
    g.fill(band);
  } else if (surface === "sanded") {
    g.poly(silhouette.facePoly);
    g.fill({ color: 0xffffff, alpha: 0.1 });
  }

  // How hard the wear map presses, rung by rung: full on unmilled
  // stock, most of the way once a cutter has been over it but the
  // surface is still sawn, a trace on a planed face — which is exactly
  // what a sanded face is missing — and none at all once it is sanded.
  const faceWear = !colorRevealed
    ? 1
    : surface === "rough"
      ? 0.55
      : surface === "smooth"
        ? 0.25
        : 0;
  if (faceWear > 0) {
    const wear = root.addChild(new Graphics());
    wear.blendMode = "multiply";
    if (tint !== undefined) wear.tint = tint;
    const wearRng = seededRandom(`${region.seed}/wear`);
    const texture = Assets.get<Texture>(
      BOARD_ROUGHNESS_TEXTURES[
        Math.floor(wearRng() * BOARD_ROUGHNESS_TEXTURES.length)
      ],
    );
    const wsx =
      (PIXELS_PER_INCH * BOARD_FACE_SCAN_WIDTH_IN) / texture.source.width;
    const wsy =
      (PIXELS_PER_INCH * BOARD_FACE_SCAN_LENGTH_IN) / texture.source.height;
    const matrix = new Matrix(
      wsx,
      0,
      0,
      wsy,
      -region.v * PIXELS_PER_INCH - width / 2,
      -region.u * PIXELS_PER_INCH - height / 2,
    );
    wear.poly(silhouette.facePoly);
    wear.fill({ texture, matrix, textureSpace: "global", alpha: faceWear });
  }

  return root;
}

/** How far across the face the weathered band reaches from the edge it
 * clings to, as a fraction of the board's width. */
const BAND_REACH = 0.45;

/**
 * The weathering left along a long edge the milling hasn't reached.
 *
 * Milling a board is two axes, not one (see Materials.ts), so a board
 * can have a jointed face and a sawn edge at the same time. The face is
 * out in the open on the jointed side and still as it came on the other,
 * and the veil fades between the two — the same fact the wavy edge is
 * already reporting, so the two halves of the drawing agree.
 *
 * Null when both long edges say the same thing: with nothing to lean on
 * there is no side for the band to be on, and the wear map carries the
 * rung by itself.
 */
function weatheredBand(board: BoardLook): FillGradient | null {
  // Jointing straightens the right edge first, so exactly one jointed
  // edge is the one case with a rough side to name — and it is the left.
  if (board.jointedEdges !== 1) return null;
  return new FillGradient({
    type: "linear",
    // Local space runs 0 at the left edge to 1 at the right.
    start: { x: 0, y: 0 },
    end: { x: BAND_REACH, y: 0 },
    textureSpace: "local",
    // Same gray at both stops, so the fade runs to clear rather than
    // through a darker version of itself on the way out.
    colorStops: [
      { offset: 0, color: new Color(WEATHERED_GRAY).setAlpha(WEATHERED_ALPHA) },
      { offset: 1, color: new Color(WEATHERED_GRAY).setAlpha(0) },
    ],
  });
}

/**
 * The outline a lying board draws, centered on its own center in
 * pixels — the very polygon its face is painted in, wavy sawn edges
 * and mitered ends included — so its shadow (materialShadow.ts) can
 * trace the same wood. Give it the seed the sprite was drawn with
 * (the material's id) and the two agree to the pixel.
 */
export function boardOutline(board: BoardLook, seed?: string): number[] {
  return boardSilhouette(board, seed ?? boardFallbackSeed(board)).facePoly;
}

/** The seed a board draws with when no caller names one. */
function boardFallbackSeed(board: BoardLook): string {
  return `${board.species}-${board.width}x${board.length}x${board.thickness}`;
}

/**
 * The board's outline, shared by the base and wear layers: wavy
 * unjointed edges (seeded so they never shimmer) and miter skews.
 */
function boardSilhouette(board: BoardLook, seed: string) {
  const { jointedEdges, ends } = board;
  const width = board.width * PIXELS_PER_INCH;
  const height = board.length * PIXELS_PER_INCH;
  const rng = seededRandom(seed);

  // Mitered ends slant across the face (miterSkews carries the sign
  // convention: same angle, same lean, so mirrored angles close a
  // frame corner)
  const skews = miterSkews(board.width, board.length, ends);
  const topSkew = skews.left * PIXELS_PER_INCH;
  const bottomSkew = skews.right * PIXELS_PER_INCH;

  // Long edges: jointing snaps them straight, right edge first
  const amp = Math.min(1.5, width * 0.12);
  const rightAmp = jointedEdges >= 1 ? 0 : amp;
  const leftAmp = jointedEdges >= 2 ? 0 : amp;
  const edgePoints = (
    x: number,
    edgeAmp: number,
    yStart: number,
    yEnd: number,
  ): [number, number][] => {
    const segments = Math.max(2, Math.round(height / 16));
    const points: [number, number][] = [];
    for (let i = 0; i <= segments; i++) {
      const y = lerp(yStart, yEnd, i / segments);
      // Ends stay near-true so corners read as crosscut clean
      const jitter = i === 0 || i === segments ? edgeAmp * 0.3 : edgeAmp;
      points.push([x + (rng() * 2 - 1) * jitter, y]);
    }
    return points;
  };
  // Positive skew pulls back the left-edge corner at the top and the
  // right-edge corner at the bottom (and vice versa for negative), so
  // both end lines slant the same way for the same angle — equal
  // angles draw parallel, mirrored angles converge into the frame
  // rail's trapezoid (long edge left for -45/+45, right for +45/-45).
  const leftEdge = edgePoints(
    -width / 2,
    leftAmp,
    -height / 2 + Math.max(0, topSkew),
    height / 2 - Math.max(0, -bottomSkew),
  );
  const rightEdge = edgePoints(
    width / 2,
    rightAmp,
    -height / 2 + Math.max(0, -topSkew),
    height / 2 - Math.max(0, bottomSkew),
  );
  return {
    width,
    height,
    topSkew,
    bottomSkew,
    facePoly: [...leftEdge, ...[...rightEdge].reverse()].flat(),
  };
}

/**
 * A board stood on its long edge, seen from above: nothing but the
 * narrow edge face (thickness × length) — the wide contact shadow is
 * what says the board stands. The edge is a window onto the board's
 * own scans, placed by its face region, so tipping a board up shows
 * the same wood it showed lying down. Same milled-state language as
 * createBoardSprite: unjointed edges weather gray, jointing lifts the
 * veil.
 */
export function drawBoardOnEdge(
  g: Graphics,
  board: BoardLook,
  seed?: string,
): void {
  const {
    width: boardWidth,
    length: boardLength,
    thickness,
    species,
    jointedEdges,
    face,
  } = board;

  g.clear();
  const width = (thickness / 4) * PIXELS_PER_INCH;
  const height = boardLength * PIXELS_PER_INCH;
  const pieceSeed = seed ?? boardFallbackSeed(board);
  const region = face ?? defaultBoardFace(pieceSeed, boardLength, boardWidth);
  const art = woodArt(species, region.seed, thickness);

  // The upturned edge face — nothing but it: the wide shadow is what
  // says the board stands
  g.rect(-width / 2, -height / 2, width, height);
  g.fill(edgeFill(art, region, boardWidth, thickness, -width / 2, -height / 2));

  // Weathering lifts as milling reveals the wood, the same way it
  // does on the face-up board
  if (jointedEdges === 0) {
    g.rect(-width / 2, -height / 2, width, height);
    g.fill({ color: WEATHERED_GRAY, alpha: 0.5 });
  }
}

/**
 * The narrow edge strip (thickness × length) the block plane's strokes
 * are constrained to, windowed onto the board's own wood — the old
 * `EdgeBandSprite`. Just the band, no shadow (drawBoardOnEdge draws
 * the standing board; this draws the surface being worked). Unjointed
 * it hides under the weathered veil; `finished` shows the grain the
 * plane brought up.
 */
export function drawBoardEdgeBand(
  g: Graphics,
  board: BoardLook,
  finished: boolean,
  seed?: string,
): void {
  const { width: boardWidth, length, thickness, species, face } = board;

  g.clear();
  const width = (thickness / 4) * PIXELS_PER_INCH;
  const height = length * PIXELS_PER_INCH;
  const pieceSeed = seed ?? boardFallbackSeed(board);
  const region = face ?? defaultBoardFace(pieceSeed, length, boardWidth);
  const art = woodArt(species, region.seed, thickness);

  g.rect(-width / 2, -height / 2, width, height);
  g.fill(edgeFill(art, region, boardWidth, thickness, -width / 2, -height / 2));
  if (!finished) {
    g.rect(-width / 2, -height / 2, width, height);
    g.fill({ color: WEATHERED_GRAY, alpha: 0.55 });
  }
}

/**
 * A board stood on its end, seen from above: nothing but the end grain —
 * the bare width × thickness cross-section a table leg presents while it
 * waits under a face-down top. A window onto a real crosscut photograph,
 * placed by the piece's seed so standing a board up doesn't reroll its
 * character, and shaded twice over because end grain drinks light.
 */
export function drawBoardOnEnd(
  g: Graphics,
  board: BoardLook,
  seed?: string,
): void {
  const { width: boardWidth, length, thickness, species } = board;

  g.clear();
  const w = boardWidth * PIXELS_PER_INCH;
  const h = (thickness / 4) * PIXELS_PER_INCH;
  const pieceSeed = seed ?? "on-end";

  const art = woodArt(species, pieceSeed, thickness);
  g.rect(-w / 2, -h / 2, w, h);
  g.fill(endFill(art, pieceSeed, boardWidth, thickness / 4, -w / 2, -h / 2));

  // End grain sits in shade against the faces around it
  for (const _ of [0, 1]) {
    g.rect(-w / 2, -h / 2, w, h);
    g.fill(TURNED_AWAY_SHADE);
  }

  // a hairline rim so the block reads as a cut face, not a fill
  g.rect(-w / 2, -h / 2, w, h);
  g.stroke({ color: 0x000000, width: 1, alpha: 0.35 });
}
